import React, { useState, useRef } from 'react';
import { Users, MapPin, Trophy, Instagram, Mail, ArrowLeft, CheckCircle, Image, Calendar, Shield, Award, Upload, X, Eye, EyeOff } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { communityTeamAPI } from '../../services/community';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

const RegisterTeam = ({ onBack, onSuccess, currentPlayer }) => {
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: '',
    location: '',
    founded_year: '',
    home_field: '',
    championships: '',
    awards: '',
    instagram_handle: '',
    bio: '',
    logo_url: '',
    recovery_email: '',
    captain_password: '',
    confirm_password: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Please upload a valid image file (JPG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image file must be less than 5MB');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const uploadData = new FormData();
      uploadData.append('file', file);
      uploadData.append('type', 'team');

      const response = await fetch(`${API_BASE_URL}/api/uploads/image?type=team`, {
        method: 'POST',
        body: uploadData,
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const data = await response.json();
      setFormData(prev => ({ ...prev, logo_url: `${API_BASE_URL}${data.url}` }));
    } catch (err) {
      setError('Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const removeLogo = () => {
    setFormData(prev => ({ ...prev, logo_url: '' }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.name) {
      setError('Team name is required');
      return;
    }

    if (!formData.recovery_email) {
      setError('Recovery email is required');
      return;
    }

    if (!formData.captain_password) {
      setError('Captain password is required');
      return;
    }

    if (formData.captain_password !== formData.confirm_password) {
      setError('Passwords do not match');
      return;
    }

    if (formData.captain_password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const submitData = {
        name: formData.name,
        location: formData.location,
        founded_year: formData.founded_year,
        home_field: formData.home_field,
        championships: formData.championships,
        awards: formData.awards,
        social_instagram: formData.instagram_handle,
        description: formData.bio,
        logo_url: formData.logo_url,
        recovery_email: formData.recovery_email,
        captain_password: formData.captain_password,
        owner_player_id: currentPlayer?.player_id || null,
      };
      
      const result = await communityTeamAPI.create(submitData);
      setSuccess(true);
      
      // Store team info
      localStorage.setItem('gwins_team', JSON.stringify(result.team));
      
      if (onSuccess) {
        setTimeout(() => onSuccess(result.team), 2000);
      }
    } catch (err) {
      setError(err.message || 'Failed to create team');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-[#0a0b0c] flex items-center justify-center p-4">
        <div className="bg-[#131416] rounded-2xl p-8 max-w-md w-full text-center border border-[#00d4aa]/30">
          <CheckCircle className="h-16 w-16 text-[#00d4aa] mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Team Registered!</h2>
          <p className="text-white/60 mb-4">{formData.name} is now part of the GWINS community!</p>
          <p className="text-sm text-white/40">Redirecting...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0b0c] py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={onBack}
            className="text-white/60 hover:text-white mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <h1 className="text-3xl font-black text-white uppercase">Create Team</h1>
          <p className="text-white/60 mt-2">Register your team and start building your roster</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-400">
              {error}
            </div>
          )}

          {/* Logo Upload */}
          <div className="bg-[#131416] rounded-xl p-6 border border-white/10">
            <h2 className="text-lg font-bold text-white mb-4 flex items-center">
              <Image className="h-5 w-5 mr-2 text-[#00d4aa]" />
              Upload Logo
            </h2>
            
            <div className="flex items-center gap-6">
              <div className="relative">
                <div className="w-24 h-24 rounded-xl bg-[#1a1c1f] border-2 border-dashed border-white/20 flex items-center justify-center overflow-hidden">
                  {formData.logo_url ? (
                    <img src={formData.logo_url} alt="Team Logo" className="w-full h-full object-cover" />
                  ) : (
                    <Image className="h-8 w-8 text-white/30" />
                  )}
                </div>
                {formData.logo_url && (
                  <button
                    type="button"
                    onClick={removeLogo}
                    className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                  >
                    <X className="h-4 w-4 text-white" />
                  </button>
                )}
              </div>
              <div className="flex-1 space-y-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="team-logo-upload"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="border-[#00d4aa]/50 text-[#00d4aa] hover:bg-[#00d4aa]/10 w-full"
                >
                  {uploading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-[#00d4aa] border-t-transparent rounded-full animate-spin mr-2" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Upload Logo
                    </>
                  )}
                </Button>
                <p className="text-white/40 text-xs">JPG, PNG, GIF or WebP. Max 5MB.</p>
              </div>
            </div>
          </div>

          {/* Team Info */}
          <div className="bg-[#131416] rounded-xl p-6 border border-white/10">
            <h2 className="text-lg font-bold text-white mb-4 flex items-center">
              <Users className="h-5 w-5 mr-2 text-[#00d4aa]" />
              Team Information
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-white/70 text-sm mb-2">Team Name *</label>
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="e.g., Thunder Squad, Viper Elite"
                  className="bg-[#1a1c1f] border-white/10 text-white"
                  required
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-white/70 text-sm mb-2">Location</label>
                  <Input
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    placeholder="City, State/Country"
                    className="bg-[#1a1c1f] border-white/10 text-white"
                  />
                </div>
                <div>
                  <label className="block text-white/70 text-sm mb-2">Founded Year</label>
                  <Input
                    name="founded_year"
                    value={formData.founded_year}
                    onChange={handleChange}
                    placeholder="e.g., 2020"
                    className="bg-[#1a1c1f] border-white/10 text-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-white/70 text-sm mb-2">Home Field</label>
                <Input
                  name="home_field"
                  value={formData.home_field}
                  onChange={handleChange}
                  placeholder="e.g., Hollywood Sports Park, SC Village"
                  className="bg-[#1a1c1f] border-white/10 text-white"
                />
              </div>
            </div>
          </div>

          {/* Achievements */}
          <div className="bg-[#131416] rounded-xl p-6 border border-white/10">
            <h2 className="text-lg font-bold text-white mb-4 flex items-center">
              <Trophy className="h-5 w-5 mr-2 text-[#00d4aa]" />
              Achievements
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-white/70 text-sm mb-2">Championships & Titles</label>
                <Input
                  name="championships"
                  value={formData.championships}
                  onChange={handleChange}
                  placeholder="e.g., NXL World Cup 2023 Champion, PSP Series Winner"
                  className="bg-[#1a1c1f] border-white/10 text-white"
                />
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-2">Awards & Achievements</label>
                <Input
                  name="awards"
                  value={formData.awards}
                  onChange={handleChange}
                  placeholder="e.g., MVP NXL Chicago 2023, All-Star Team Selection, Top Gun Award"
                  className="bg-[#1a1c1f] border-white/10 text-white"
                />
              </div>
            </div>
          </div>

          {/* Social & Bio */}
          <div className="bg-[#131416] rounded-xl p-6 border border-white/10">
            <h2 className="text-lg font-bold text-white mb-4 flex items-center">
              <Instagram className="h-5 w-5 mr-2 text-[#00d4aa]" />
              Social & Bio
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-white/70 text-sm mb-2">Instagram Handle</label>
                <Input
                  name="instagram_handle"
                  value={formData.instagram_handle}
                  onChange={handleChange}
                  placeholder="@yourhandle"
                  className="bg-[#1a1c1f] border-white/10 text-white"
                />
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-2">Bio</label>
                <textarea
                  name="bio"
                  value={formData.bio}
                  onChange={handleChange}
                  placeholder="Tell us about your team..."
                  rows={4}
                  className="w-full bg-[#1a1c1f] border border-white/10 text-white rounded-md px-3 py-2 resize-none"
                />
                <p className="text-white/40 text-xs mt-1">
                  Example: "We're a competitive D3 team based in Miami, founded in 2020. Our focus is on speedball tournaments in the Southeast region. We've won multiple regional events and are always looking for dedicated players who want to compete at the next level."
                </p>
              </div>
            </div>
          </div>

          {/* Captain Security */}
          <div className="bg-[#131416] rounded-xl p-6 border border-[#00d4aa]/30">
            <h2 className="text-lg font-bold text-white mb-4 flex items-center">
              <Shield className="h-5 w-5 mr-2 text-[#00d4aa]" />
              Captain Security
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-white/70 text-sm mb-2">Recovery Email *</label>
                <Input
                  name="recovery_email"
                  type="email"
                  value={formData.recovery_email}
                  onChange={handleChange}
                  placeholder="captain@example.com"
                  className="bg-[#1a1c1f] border-white/10 text-white"
                  required
                />
                <p className="text-white/40 text-xs mt-1">Required for password recovery if you forget the captain password</p>
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-2">Captain Password *</label>
                <div className="relative">
                  <Input
                    name="captain_password"
                    type={showPassword ? "text" : "password"}
                    value={formData.captain_password}
                    onChange={handleChange}
                    placeholder="Set a password to edit team later"
                    className="bg-[#1a1c1f] border-white/10 text-white pr-10"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-white/40 text-xs mt-1">Remember this password! Only captains with this password can edit the team</p>
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-2">Confirm Password *</label>
                <div className="relative">
                  <Input
                    name="confirm_password"
                    type={showConfirmPassword ? "text" : "password"}
                    value={formData.confirm_password}
                    onChange={handleChange}
                    placeholder="Confirm captain password"
                    className="bg-[#1a1c1f] border-white/10 text-white pr-10"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60"
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Captain Info - Only show if logged in with player profile */}
          {currentPlayer && (
            <div className="bg-[#00d4aa]/10 rounded-xl p-4 border border-[#00d4aa]/30">
              <p className="text-white/70 text-sm">
                <strong className="text-white">Team Captain:</strong> {currentPlayer.name}
              </p>
              <p className="text-white/50 text-xs mt-1">
                You will be added as the team captain and first member
              </p>
            </div>
          )}

          {/* Submit */}
          <div className="flex gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={onBack}
              className="flex-1 border-white/20 text-white hover:bg-white/5"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-[#00d4aa] hover:bg-[#33e0bb] text-[#0a0b0c] font-bold"
            >
              {loading ? 'Creating...' : 'CREATE TEAM'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RegisterTeam;
